'use client'

import { useState } from 'react'
import { BookmarkIcon, Loader2Icon } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  HybridTooltip,
  HybridTooltipContent,
  HybridTooltipTrigger,
} from '@/components/ui/hybrid-tooltip'
import { useUserTracking } from '@/providers/UserTracking'
import { cn } from '@/utilities/ui'

import { TrackTagLoginPrompt } from './TrackTagLoginPrompt'
import { TrackTagTrackConfirm } from './TrackTagTrackConfirm'
import { TrackTagUntrackConfirm } from './TrackTagUntrackConfirm'

export const TrackTag = ({
  billId,
  isTracked: isTrackedProp,
  onToggleTrack: onToggleTrackProp,
  variant = 'icon',
  customClassName,
  size = 'default',
}: {
  billId: number
  isTracked: boolean
  /** When undefined, falls back to useUserTracking() so BillInfo etc. work without passing it. */
  onToggleTrack?: (billId: number) => Promise<void>
  customClassName?: string
  variant?: 'icon' | 'full'
  size?: 'small' | 'default'
}) => {
  const trackingContext = useUserTracking()
  const onToggleTrack = onToggleTrackProp ?? trackingContext?.onToggleTrack
  const trackedBillIds = trackingContext?.trackedBillIds
  const isTracked = isTrackedProp ?? (trackedBillIds != null && trackedBillIds.includes(billId))

  const [loading, setLoading] = useState(false)
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const [buttonHover, setButtonHover] = useState(false)
  const isLoggedIn = onToggleTrack != null

  const handleTrackClick = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!isLoggedIn) return
    if (loading) return
    if (!isTracked) {
      await handleTrackConfirm()
      return
    }
    setTooltipOpen(true)
  }

  const handleTrackConfirm = async () => {
    if (!onToggleTrack || loading) return
    setLoading(true)
    try {
      await onToggleTrack(billId)
      setTooltipOpen(false)
    } finally {
      setLoading(false)
    }
  }

  const handleUntrackConfirm = async () => {
    if (!onToggleTrack || loading) return
    setLoading(true)
    try {
      await onToggleTrack(billId)
      setTooltipOpen(false)
    } finally {
      setLoading(false)
    }
  }

  const variantClassNames = {
    icon: {
      default:
        'text-muted-foreground -ml-2.5 absolute top-0 right-0 z-10 inline-flex transition-colors border-none bg-transparent',
      tracked:
        'text-accent-2 [&_path]:fill-accent-2 hover:text-destructive hover:[&_path]:fill-destructive',
      trackedHover:
        'text-destructive [&_path]:fill-destructive [&_path]:text-destructive hover:text-destructive hover:[&_path]:fill-destructive hover:[&_path]:text-destructive',
      untracked: 'hover:text-accent-2 hover:[&_path]:fill-accent-2',
    },
    full: {
      default: 'shrink-0 inline-flex transition-colors',
      tracked:
        'bg-evergreen-100 text-evergreen-800 [&_path]:text-accent-2 [&_path]:fill-accent-2 hover:text-destructive hover:[&_path]:fill-destructive hover:[&_path]:text-destructive',
      trackedHover:
        'text-destructive [&_path]:fill-destructive [&_path]:text-destructive hover:text-destructive hover:[&_path]:fill-destructive hover:[&_path]:text-destructive bg-destructive/10',
      untracked: '[&_path]:text-accent-2 hover[&_path]:text-accent-2 hover:[&_path]:fill-accent-2',
    },
  }
  const showHoverStyle = buttonHover || tooltipOpen

  const triggerClassName = cn(
    variantClassNames[variant].default,
    variantClassNames[variant][isTracked ? 'tracked' : 'untracked'],
    variant === 'full' && isTracked && showHoverStyle && variantClassNames.full.trackedHover,
    variant === 'icon' && isTracked && showHoverStyle && variantClassNames.icon.trackedHover,
    customClassName,
  )

  const handleTooltipOpenChange = (open: boolean) => {
    setTooltipOpen(open)
    if (!open) setButtonHover(false)
  }

  const button =
    variant === 'icon' ? (
      <Button
        variant="iconOutline"
        size="small"
        icon={false}
        type="button"
        className={triggerClassName}
        onClick={isLoggedIn ? handleTrackClick : undefined}
        disabled={loading}
        onMouseEnter={() => setButtonHover(true)}
        onMouseLeave={() => setButtonHover(false)}
      >
        <span className="sr-only">{isTracked ? 'Untrack bill' : 'Track bill'}</span>
        {loading ? (
          <Loader2Icon
            className={cn(
              'size-4.5 shrink-0 animate-spin',
              size === 'default' && 'md:size-4.5',
              size === 'small' && 'md:size-3.5',
            )}
            aria-hidden
          />
        ) : (
          <BookmarkIcon
            className={cn(
              'size-4.5 shrink-0',
              size === 'default' && 'md:size-4.5',
              size === 'small' && 'md:size-3.5',
            )}
            aria-hidden
          />
        )}
      </Button>
    ) : (
      <Button
        variant="outline"
        className={triggerClassName}
        onClick={isLoggedIn ? handleTrackClick : undefined}
        disabled={loading}
        icon={false}
        onMouseEnter={() => setButtonHover(true)}
        onMouseLeave={() => setButtonHover(false)}
      >
        {isTracked && !showHoverStyle
          ? 'Tracking'
          : isTracked && showHoverStyle
            ? 'Untrack'
            : 'Track'}
        <BookmarkIcon className="size-4.5 shrink-0 md:size-3.5" aria-hidden />
      </Button>
    )

  return (
    <HybridTooltip
      open={isLoggedIn && isTracked ? tooltipOpen : undefined}
      onOpenChange={handleTooltipOpenChange}
    >
      <HybridTooltipTrigger asChild>{button}</HybridTooltipTrigger>
      <HybridTooltipContent>
        {isLoggedIn ? (
          isTracked ? (
            <TrackTagUntrackConfirm onConfirm={handleUntrackConfirm} loading={loading} />
          ) : (
            <TrackTagTrackConfirm />
          )
        ) : (
          <TrackTagLoginPrompt />
        )}
      </HybridTooltipContent>
    </HybridTooltip>
  )
}
